import React, { useState } from 'react';
import { Button, Loader } from '@wix/design-system';
import {
  QueryDecorator,
  useEssentials,
  useServices,
  useSiteContext,
  useTranslation,
} from '@wix/app-market-core';
import { useUpgradeButtonBiEvents } from './use-upgrade-bi-event';
import { hasBenefitOrBundleBadge } from '../../pages/app-page/utils/badges';
import { getAppPackagePickerUrl } from './get-app-package-picker-url';
import { UpgradeModal } from '../upgrade-modal/upgrade-modal';
import type {
  UpgradeAppButtonProps,
  UpgradeAppButtonRenderProps,
} from '@wix/app-market-core-components-types';

export const UPGRADE_APP_BUTTON_DATA_HOOK = 'upgrade-app-button';
const UPGRADE_APP_MODAL_EXPERIMENT = 'specs.app-market.UpgradeAppModal';

export function UpgradeAppButton({
  app,
  size,
  origin,
  render,
  appPageType,
  onUpgradeModalClose,
}: UpgradeAppButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const services = useServices();
  const { experiments } = useEssentials();
  const { t } = useTranslation();
  const { metaSiteId } = useSiteContext();

  const shouldUseModal =
    !app.externalPricingPageUrl &&
    experiments.enabled(UPGRADE_APP_MODAL_EXPERIMENT);

  const appPackagePickerUrl = getAppPackagePickerUrl({
    appId: app.id,
    metaSiteId: metaSiteId as string,
    isModalEnabled: shouldUseModal,
  });
  const showRedeemOfferButton = hasBenefitOrBundleBadge({
    appBadges: app.appBadges,
  });
  const { reportUpgradeClick, reportFreeTrialClick } = useUpgradeButtonBiEvents(
    {
      app,
      origin,
      showRedeemOfferButton,
      appPageType,
    },
  );

  const handleUpgradeClick = () => {
    if (shouldUseModal) {
      setIsModalOpen(true);
    } else {
      window.open(appPackagePickerUrl, '_blank');
    }
  };

  return (
    <>
      <QueryDecorator
        queryFn={async () => {
          const freeTrialAppIds = await (metaSiteId
            ? services.getEligibleAppsForFreeTrial()
            : Promise.resolve([]));

          return freeTrialAppIds.includes(app.id);
        }}
        queryKey={`upgrade-app-button-${app.id}`}
        csrOnly
      >
        {({ data: isAppEligibleForFreeTrial, isLoading }) => {
          const text = isAppEligibleForFreeTrial
            ? t('app.page.upgrade.cta.label.free.trial')
            : showRedeemOfferButton
            ? t('app.page.upgrade.cta.label.benefit')
            : t('app.page.upgrade.cta.label');

          const commonProps: UpgradeAppButtonRenderProps = {
            disabled: isLoading,
            skin: isAppEligibleForFreeTrial ? 'standard' : 'premium',
            onClick: () => {
              if (isAppEligibleForFreeTrial) {
                reportFreeTrialClick();
              } else {
                reportUpgradeClick();
              }
              handleUpgradeClick();
            },
            isLoading,
            text,
          };

          return (
            <>
              {render?.(commonProps) || (
                <Button
                  {...commonProps}
                  size={size}
                  dataHook={UPGRADE_APP_BUTTON_DATA_HOOK}
                >
                  {isLoading ? <Loader size="tiny" /> : text}
                </Button>
              )}
            </>
          );
        }}
      </QueryDecorator>

      {shouldUseModal && isModalOpen && (
        <UpgradeModal
          app={{
            id: app.id,
            externalPricingPageUrl: app.externalPricingPageUrl,
          }}
          onClose={() => {
            onUpgradeModalClose?.();
            setIsModalOpen(false);
          }}
          isOpen={isModalOpen}
          ppOrigin={origin}
        />
      )}
    </>
  );
}
